import React, { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
interface Offer {
  id: number;
  badge?: string;
  type: string;
  title: string;
  subtitle: string;
  features: string[];
  description: string;
  oldPrice: string;
  price: string;
  url: string;
  image: string;
}
const offers: Offer[] = [{
  id: 1,
  type: 'OFERTA PRINCIPAL',
  title: 'Mesas y Espejos Infinitos',
  subtitle: '🚀 Todo para dominar el arte infinito',
  features: ['Libro Principal Mesas y Espejos', '5 Bonus Exclusivos'],
  description: 'Ideal para empezar desde cero.',
  oldPrice: '$39.990',
  price: '$19.990',
  url: 'https://www.digiital-factory.shop/cart/51419692302642:1',
  image: 'https://cdn.magicpatterns.com/uploads/aDMv55B1qGuhWpovQUWGLB/kit_mesas_nomas.webp'
}, {
  id: 2,
  type: 'COMBO ESPECIAL',
  title: 'Mesas y Espejos + Neon Flex',
  subtitle: '💎 Accedé a 2 capacitaciones completas por una diferencia mínima:',
  features: ['Mesas y Espejos Infinitos + 5 Bonus', 'Neon Flex Digital + 6 Bonus'],
  description: 'Más valor. Más contenido. Más oportunidades.',
  oldPrice: '$55.990',
  price: '$25.990',
  url: 'https://www.digiital-factory.shop/cart/51419993440562:1',
  image: 'https://cdn.magicpatterns.com/uploads/6bCr76dvFNPeqckCw4jvix/imagen_de_kit_completo_22B11.webp'
}, {
  id: 3,
  badge: '★ Más elegido',
  type: 'COMBO SUPER ESPECIAL',
  title: 'Mesas y Espejos + Neon Flex + Resina Epoxica',
  subtitle: '💎 Accedé a 3 capacitaciones completas por una diferencia mínima:',
  features: ['Mesas y Espejos Infinitos + 5 Bonus', 'Neon Flex Digital + 6 Bonus', 'Resina Epoxica + 5 Bonus'],
  description: 'Más valor. Más contenido. Más oportunidades.',
  oldPrice: '$79.990',
  price: '$29.990',
  url: 'https://www.digiital-factory.shop/cart/51419993833778:1',
  image: 'https://cdn.magicpatterns.com/uploads/7TpeCs2KLtjefgENVJ6nci/imagen_de_kit_completo_32B16.webp'
}];
export function PriceBox() {
  const [selectedId, setSelectedId] = useState(3);
  const [viewers, setViewers] = useState(27);
  const [spots, setSpots] = useState(7);
  const [isVisible, setIsVisible] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const viewersInterval = setInterval(() => {
      setViewers(prev => Math.max(18, prev + (Math.random() > 0.5 ? 1 : -1)));
    }, 4000);
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        // Show when the section is in view
        setIsVisible(rect.top < window.innerHeight && rect.bottom > 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      clearInterval(viewersInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Sequential purchase notifications while section is visible
  useEffect(() => {
    let timers: NodeJS.Timeout[] = [];
    if (isVisible) {
      // 1st alert at 10s
      const t1 = setTimeout(() => {
        setShowPurchase(true);
        setSpots(6);
        timers.push(setTimeout(() => setShowPurchase(false), 4000));
      }, 10000);
      // 2nd alert at 25s (10s + 15s later)
      const t2 = setTimeout(() => {
        setShowPurchase(true);
        timers.push(setTimeout(() => setShowPurchase(false), 4000));
      }, 25000);
      // Recurring alerts every 20s starting after the 2nd one (at 45s, 65s, etc.)
      const interval = setInterval(() => {
        setShowPurchase(true);
        timers.push(setTimeout(() => setShowPurchase(false), 4000));
      }, 20000);
      // Initial offset for interval to start after t2
      // Since setInterval starts after the delay, and we want it at 45s,
      // we can either use a nested timeout or just let it run.
      // To be precise: t2 is at 25s. We want next at 45s.
      // So interval with 20s delay starting at 25s is perfect.
      timers.push(t1, t2, interval);
    } else {
      setShowPurchase(false);
    }
    return () => {
      timers.forEach(t => {
        if (typeof t === 'number' || typeof t === 'object') {
          clearTimeout(t as any);
          clearInterval(t as any);
        }
      });
    };
  }, [isVisible]);
  const selectedOffer = offers.find(o => o.id === selectedId) || offers[2];
  return <section id="pricing" ref={sectionRef} className="py-12 px-4 bg-[#0a0514] relative z-10 overflow-hidden pt-[20px] pb-[20px]" data-id="element-187">
      {/* Floating Social Proof Notification */}
      <AnimatePresence mode="wait" data-id="element-188">
        {isVisible && <motion.div key={showPurchase ? 'purchase' : 'stats'} initial={{
        y: -20,
        opacity: 0,
        x: '-50%'
      }} animate={{
        y: 0,
        opacity: 1,
        x: '-50%'
      }} exit={{
        y: -20,
        opacity: 0,
        x: '-50%'
      }} transition={{
        duration: 0.6
      }} className="fixed top-[8%] left-1/2 z-[100] w-max max-w-[90vw] flex flex-col items-center gap-3" data-id="element-189">
            {/* Main Stats Pill */}
            <div className="bg-white rounded-[30px] px-7 py-3.5 shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-100 flex flex-col items-start gap-1" data-id="element-190">
              {/* Line 1: Viewers */}
              <div className="flex items-center gap-3" data-id="element-191">
                <div className="w-2.5 h-2.5 rounded-full bg-[#22c55e] shrink-0 shadow-[0_0_8px_rgba(34,197,94,0.4)]" data-id="element-192" />
                <span className="text-[13px] font-bold text-[#6b21a8] uppercase tracking-tight flex items-center gap-2" data-id="element-193">
                  👥 {viewers} PERSONAS MIRANDO
                </span>
              </div>
              {/* Line 2: Spots */}
              <div className="flex items-center gap-3" data-id="element-194">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ef4444] shrink-0 shadow-[0_0_8px_rgba(239,68,68,0.4)]" data-id="element-195" />
                <span className="text-[13px] font-bold text-[#6b21a8] uppercase tracking-tight flex items-center gap-2" data-id="element-196">
                  🔥 CUPOS DISPONIBLES: {spots}
                </span>
              </div>
            </div>

            {/* Purchase Notification - Appears below */}
            <AnimatePresence data-id="element-197">
              {showPurchase && <motion.div initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} className="bg-[#22c55e] rounded-full px-8 py-3 shadow-[0_10px_40px_rgba(34,197,94,0.3)] flex items-center border border-white/20 pl-[10px] pr-[10px] gap-[0px]" data-id="element-198">
                  <ShoppingCart className="w-5 h-5 text-white fill-white" data-id="element-199" />
                  <span className="text-[14px] font-black text-white uppercase tracking-wider" data-id="element-200">
                    ¡ALGUIEN ACABA DE COMPRAR!
                  </span>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-md mx-auto" data-id="element-201">
        <h2 className="text-white text-2xl font-black text-center mb-8 uppercase tracking-tight" data-id="element-202">
          Elegí tu oferta especial
        </h2>

        {/* Offer Cards */}
        <div className="space-y-4 mb-8" data-id="element-203">
          {offers.map(offer => <div key={offer.id} onClick={() => setSelectedId(offer.id)} className={`relative cursor-pointer transition-all duration-300 rounded-3xl p-6 border-2 ${selectedId === offer.id ? 'bg-white border-[#00f0ff] shadow-[0_0_30px_rgba(0,240,255,0.2)]' : 'bg-white/95 border-gray-200 hover:border-gray-300'}`} data-id="element-204">
              {offer.badge && <div className="absolute -top-3 right-6 bg-yellow-400 text-black text-[10px] font-black px-3 py-1 rounded-full uppercase shadow-sm" data-id="element-205">
                  {offer.badge}
                </div>}

              <div className="mb-4" data-id="element-206">
                <p className={cn(`text-[10px] font-black mb-1 ${selectedId === offer.id ? 'text-purple-600' : 'text-purple-400'}`, 'text-[12px]')} data-id="element-207">
                  {offer.type}
                </p>
                <h3 className="text-gray-900 font-black mb-2 text-[23px]" data-id="element-208">
                  {offer.title}
                </h3>
                <p className="text-blue-600 font-bold mb-3 text-[13px]" data-id="element-209">
                  {offer.subtitle}
                </p>
                <ul className="space-y-1.5 mb-4" data-id="element-210">
                  {offer.features.map((feature, i) => <li key={i} className="flex items-center gap-2 text-gray-700 font-medium text-[15px]" data-id="element-211">
                      <div className="w-1 h-1 rounded-full bg-black" data-id="element-212" />
                      {feature}
                    </li>)}
                </ul>
                <p className="text-gray-500 italic mb-4 text-[12px]" data-id="element-213">
                  {offer.description}
                </p>
                <div className="flex items-baseline gap-2" data-id="element-214">
                  <span className="text-red-500 line-through font-bold text-[16px]" data-id="element-215">
                    {offer.oldPrice}
                  </span>
                  <span className="text-[#00c853] font-black text-[33px]" data-id="element-216">
                    {offer.price}
                  </span>
                </div>
              </div>

              <button className={`w-full py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all border-2 ${selectedId === offer.id ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`} data-id="element-217">
                {selectedId === offer.id ? 'SELECCIONADO' : 'SELECCIONAR'}
              </button>
            </div>)}
        </div>

        {/* Selected Offer Image */}
        <div className="mb-8 overflow-hidden rounded-2xl shadow-2xl border border-white/10 bg-white/5" data-id="element-218">
          <AnimatePresence mode="wait" data-id="element-219">
            <motion.img key={selectedOffer.id} src={selectedOffer.image} alt={selectedOffer.title} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.4
          }} className="w-full h-auto block" data-id="element-220" />
          </AnimatePresence>
        </div>

        {/* Final CTA Button */}
        <a href={selectedOffer.url} className="block w-full bg-gradient-to-r from-[#ff4081] to-[#7c4dff] text-white text-center py-6 rounded-2xl font-black text-xl uppercase tracking-wider shadow-[0_10px_40px_rgba(124,77,255,0.4)] hover:scale-[1.02] transition-transform mb-6" data-id="element-221">
          ¡COMPRAR AHORA!
        </a>

        {/* Trust Footer */}
        <div className="text-center space-y-4" data-id="element-222">
          <div className="flex items-center justify-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-223">
            <span data-id="element-224">🔒 PAGO SEGURO</span>
            <span data-id="element-225">•</span>
            <span data-id="element-226">ACCESO INMEDIATO</span>
            <span data-id="element-227">•</span>
            <span data-id="element-228">7 DÍAS DE GARANTÍA</span>
          </div>

          {/* Mercado Pago / Leader Section */}
          <div className="w-full overflow-hidden rounded-3xl shadow-lg" data-id="element-229">
            <img src="https://cdn.magicpatterns.com/uploads/xdPsaSviSJenxkyJSsN7Sa/ChatGPT_Image_29_may_2026,_16_20_08.webp" alt="MercadoLíder Platinum y Garantía" className="w-full h-auto block" data-id="element-230" />
          </div>

          <div className="flex justify-center gap-8 pt-4" data-id="element-231">
            <div className="flex flex-col items-center gap-1" data-id="element-232">
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center" data-id="element-233">
                🛡️
              </div>
              <p className="text-[8px] font-black text-gray-500 uppercase" data-id="element-234">
                Compra Segura
              </p>
            </div>
            <div className="flex flex-col items-center gap-1" data-id="element-235">
              <div className="w-10 h-10 bg-yellow-50 rounded-full flex items-center justify-center" data-id="element-236">
                🏅
              </div>
              <p className="text-[8px] font-black text-gray-500 uppercase" data-id="element-237">
                Garantía 7 Días
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}